var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/react/context.tsx
import * as React from "react";

// src/actions/index.ts
var actions_exports = {};
__export(actions_exports, {
  appleSSO: () => appleSSO,
  delete: () => deleteAccount,
  getLinkedSSO: () => getLinkedSSO,
  googleSSO: () => googleSSO,
  login: () => login,
  logout: () => logout,
  recover: () => recover,
  refresh: () => refresh,
  resendVerification: () => resendVerification,
  signup: () => signup,
  spotifyLink: () => spotifyLink,
  spotifySSO: () => spotifySSO,
  spotifyUnlink: () => spotifyUnlink
});

// src/graphql/actions.ts
async function socialLogin(client, data) {
  const { userSocialLogin } = await client.sdk.userSocialLogin(data);
  if (!userSocialLogin) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (userSocialLogin.error) {
    throw Error(`Got error: ${JSON.stringify(userSocialLogin.error)}`, {
      cause: userSocialLogin.error
    });
  }
  if (!userSocialLogin.isValid && userSocialLogin.userDetails) {
    return {
      hasAccount: false,
      isVerified: Boolean(userSocialLogin.isVerified),
      details: userSocialLogin.userDetails
    };
  }
  if (!userSocialLogin?.isValid) {
    throw Error("Got invalid response", {
      cause: userSocialLogin?.error || "unknown"
    });
  }
  if (!userSocialLogin.token) {
    throw Error("Missing token", {
      cause: userSocialLogin?.error || "unknown"
    });
  }
  await sync(client, userSocialLogin.token);
  return {
    token: userSocialLogin.token,
    details: userSocialLogin.userDetails || {},
    hasAccount: true,
    isVerified: Boolean(userSocialLogin.isVerified)
  };
}
async function getUser(client) {
  const { getCurrentUser } = await client.sdk.getCurrentUser();
  if (!getCurrentUser) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  return {
    ...getCurrentUser
  };
}

// src/core/jwt.ts
import jwt from "jsonwebtoken";
function decode(token) {
  const decoded = jwt.decode(token, {
    json: true
  });
  if (!decoded) {
    throw Error("Missing decoded data");
  }
  return decoded;
}
function isExpired(token) {
  const { tokenExpiration } = typeof token === "string" ? decode(token) : token;
  if (typeof tokenExpiration !== "string") {
    throw Error("Missing tokenExpiration property in jwt payload");
  }
  const tokenExpirationTimestamp = new Date(tokenExpiration).getTime();
  const nowTimestamp = (/* @__PURE__ */ new Date()).getTime();
  const difference = tokenExpirationTimestamp - nowTimestamp;
  return difference <= 0;
}

// src/core/store.ts
var cookieKey = "_auth";
var storeTokenKey = "auth-token";
var storeDataKey = "_auth-primaveralabs-storage-data";
var syncEventKey = "_auth-primaveralabs-storage-sync-event";
var store = {
  get: () => {
    const token = localStorage.getItem(storeTokenKey);
    const data = localStorage.getItem(storeDataKey);
    return {
      token,
      data: data ? JSON.parse(data) : null
    };
  },
  set: (token, data) => {
    localStorage.setItem(storeTokenKey, token);
    localStorage.setItem(storeDataKey, JSON.stringify(data));
  },
  del: () => {
    localStorage.removeItem(storeTokenKey);
    localStorage.removeItem(storeDataKey);
  }
};
async function sync(client, token) {
  client.setHeader("authorization", token ? `Bearer ${token}` : "");
  if (token) {
    document.cookie = `${cookieKey}=${token}; Secure; HttpOnly`;
    const decodedToken = decode(token);
    const userData = await getUser(client);
    store.set(token, { ...decodedToken, ...userData });
  } else {
    document.cookie = `${cookieKey}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; HttpOnly`;
    store.del();
  }
  window.dispatchEvent(new Event(syncEventKey));
}

// src/actions/delete.ts
async function deleteAccount(client, data) {
  const { userDeleteAccount } = await client.sdk.userDeleteAccount(data);
  if (!userDeleteAccount) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (userDeleteAccount.error) {
    throw Error(`Got error: ${JSON.stringify(userDeleteAccount.error)}`, {
      cause: userDeleteAccount.error
    });
  }
  if (!userDeleteAccount?.isDeleted) {
    throw Error("Got invalid response", {
      cause: userDeleteAccount?.error || "unknown"
    });
  }
  await sync(client);
  return {
    isDeleted: userDeleteAccount.isDeleted
  };
}

// src/actions/getLinkedSSO.ts
async function getLinkedSSO(client, data) {
  const { userSocialLinked } = await client.sdk.userSocialLinked(data);
  if (!userSocialLinked) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  return {
    isValid: Boolean(userSocialLinked.valid),
    socialNetwork: userSocialLinked.socialnetwork
  };
}

// src/actions/login.ts
async function login(client, data) {
  const { userWebLogin } = await client.sdk.userWebLogin(data);
  if (!userWebLogin) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (userWebLogin.error) {
    throw Error(`Got error: ${JSON.stringify(userWebLogin.error)}`, {
      cause: userWebLogin.error
    });
  }
  if (!userWebLogin?.isValid) {
    throw Error("Got invalid response", {
      cause: userWebLogin?.error || "unknown"
    });
  }
  if (!userWebLogin.token) {
    throw Error("Missing token", {
      cause: userWebLogin?.error || "unknown"
    });
  }
  await sync(client, userWebLogin.token);
  return {
    token: userWebLogin.token,
    isVerified: Boolean(userWebLogin.isVerified)
  };
}

// src/actions/logout.ts
async function logout(client) {
  await sync(client, null);
}

// src/actions/recover.ts
async function recover(client, data) {
  const { userPasswordRecoverUserSendsEmail } = await client.sdk.userPasswordRecover(data);
  if (!userPasswordRecoverUserSendsEmail) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (userPasswordRecoverUserSendsEmail.error) {
    throw Error(
      `Got error: ${JSON.stringify(userPasswordRecoverUserSendsEmail.error)}`,
      {
        cause: userPasswordRecoverUserSendsEmail.error
      }
    );
  }
  if (!userPasswordRecoverUserSendsEmail?.isValid) {
    throw Error("Got invalid response", {
      cause: userPasswordRecoverUserSendsEmail?.error || "unknown"
    });
  }
  return {
    isValid: userPasswordRecoverUserSendsEmail.isValid
  };
}

// src/actions/refresh.ts
async function refresh(client, data) {
  client.setHeader("authorization", `Bearer ${data.token}`);
  const { refetchUserToken } = await client.sdk.refetchUserToken();
  if (!refetchUserToken) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (refetchUserToken.error) {
    throw Error(`Got error: ${JSON.stringify(refetchUserToken.error)}`, {
      cause: refetchUserToken.error
    });
  }
  if (!refetchUserToken?.isValid) {
    throw Error("Got invalid response", {
      cause: refetchUserToken?.error || "unknown"
    });
  }
  if (!refetchUserToken.token) {
    throw Error("Missing token", {
      cause: refetchUserToken?.error || "unknown"
    });
  }
  await sync(client, refetchUserToken.token);
  return {
    token: refetchUserToken.token
  };
}

// src/actions/resendVerification.ts
async function resendVerification(client, data) {
  const { userWebVerification } = await client.sdk.userWebVerification(data);
  if (!userWebVerification) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (userWebVerification.error) {
    throw Error(`Got error: ${JSON.stringify(userWebVerification.error)}`, {
      cause: userWebVerification.error
    });
  }
  if (!userWebVerification?.isValid) {
    throw Error("Got invalid response", {
      cause: userWebVerification?.error || "unknown"
    });
  }
  return {
    isValid: userWebVerification.isValid
  };
}

// src/actions/signup.ts
async function signup(client, data) {
  const { userCheckEmail } = await client.sdk.userCheckEmail({
    email: data.email
  });
  const valid = userCheckEmail?.valid || false;
  if (!valid) {
    throw Error(`Invalid email: ${data.email}`);
  }
  const { userSignup } = await client.sdk.userSignup({
    input: data
  });
  if (!userSignup) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (userSignup.error) {
    throw Error(`Got error: ${JSON.stringify(userSignup.error)}`, {
      cause: userSignup.error
    });
  }
  if (!userSignup?.isValid) {
    throw Error("Got invalid response", {
      cause: userSignup?.error || "unknown"
    });
  }
  if (!userSignup.token) {
    throw Error("Missing token", {
      cause: userSignup?.error || "unknown"
    });
  }
  await sync(client, userSignup.token);
  return {
    token: userSignup.token
  };
}

// src/actions/ssoApple.ts
function loadAppleSSOScript() {
  const tag = document.createElement("script");
  tag.async = true;
  tag.defer = true;
  tag.type = "text/javascript";
  tag.src = `https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js`;
  tag.onerror = (error) => {
    throw Error(
      `Error loading Apple SSO script: ${JSON.stringify(error, null, 2)}`
    );
  };
  tag.onload = () => {
    AppleID.auth.init({
      clientId: "com.primaverasound.barcelona",
      scope: "name email",
      redirectURI: `${window?.location.origin}/en`,
      usePopup: true
    });
  };
  document.body.appendChild(tag);
  return () => {
    document.body.removeChild(tag);
  };
}
async function appleSSO(client, data) {
  const user = await AppleID.auth.signIn({
    redirectURI: data?.redirectUri || `${window?.location.origin}/en`
  });
  return socialLogin(client, {
    socialnetwork: "apple",
    tokenId: user.authorization.id_token,
    redirectUri: data?.redirectUri || `${window.location.origin}/en`
  });
}

// src/actions/ssoGoogle.ts
async function getAuthInstance() {
  let auth = window.gapi.auth2.getAuthInstance();
  auth = await window.gapi.auth2.init({
    client_id: `374001476805-jdr4d87slvoaobdajc0bas161r661rfs.apps.googleusercontent.com`,
    redirect_uri: `${window?.location.origin}/en`
  });
  return auth;
}
function loadGoogleSSOScript() {
  const tag = document.createElement("script");
  tag.async = true;
  tag.defer = true;
  tag.type = "text/javascript";
  tag.src = `https://apis.google.com/js/api.js`;
  tag.onerror = (error) => {
    throw Error(
      `Error loading Google SSO script: ${JSON.stringify(error, null, 2)}`
    );
  };
  tag.onload = () => {
    window.gapi.load("auth2", getAuthInstance);
  };
  document.body.appendChild(tag);
  return () => {
    document.body.removeChild(tag);
  };
}
async function googleSSO(client, data) {
  const auth = await getAuthInstance();
  let user;
  user = await auth.signIn({
    redirect_uri: data?.redirectUri || `${window.location.origin}/en`,
    prompt: "consent select_account"
  });
  const userAuthResponse = user.getAuthResponse();
  return socialLogin(client, {
    socialnetwork: "google",
    tokenId: userAuthResponse.id_token,
    redirectUri: data?.redirectUri || `${window.location.origin}/en`
  });
}

// src/core/popup.ts
var CHECK_INTERVAL_MS = 100;
async function createSocialAuthPopup({
  url,
  title,
  features = "width=600, height=1000"
}) {
  return new Promise((resolve, reject) => {
    const popup = window.open(url, title, features);
    if (!popup)
      throw Error("Missing popup reference");
    const interval = setInterval(() => {
      try {
        if (popup.closed)
          return;
        const { searchParams } = new URL(popup.location.href);
        const code = searchParams.get("code");
        if (!code)
          return;
        resolve(code);
        clearInterval(interval);
        popup.close();
      } catch (error) {
        if (error instanceof DOMException && error.code === error.SECURITY_ERR || popup.closed) {
          return;
        }
        console.warn("createSocialAuthPopup error:", error);
        reject(error);
        clearInterval(interval);
      }
    }, CHECK_INTERVAL_MS);
  });
}

// src/actions/ssoSpotify.ts
function getSocialAuthUrl() {
  const url = new URL("https://accounts.spotify.com/authorize");
  url.searchParams.set("redirect_uri", `${window?.location.origin}/en`);
  url.searchParams.set("client_id", "76a75784cbb541e69bc57569652757e2");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("show_dialog", "true");
  url.searchParams.set(
    "scope",
    "user-read-private user-read-email user-top-read playlist-read-private user-follow-read"
  );
  return url.toString();
}
async function spotifySSO(client, data) {
  const tokenId = await createSocialAuthPopup({
    url: getSocialAuthUrl(),
    title: "spotify-authorization"
  });
  return socialLogin(client, {
    socialnetwork: "spotify",
    tokenId,
    redirectUri: data?.redirectUri || `${window.location.origin}/en`
  });
}
async function spotifyLink(client, data) {
  const tokenId = await createSocialAuthPopup({
    url: getSocialAuthUrl(),
    title: "spotify-authorization"
  });
  const { userLinkSpotify } = await client.sdk.userLinkSpotify({
    tokenId,
    userEmail: data.email,
    redirectUri: data?.redirectUri || `${window.location.origin}/en`
  });
  if (!userLinkSpotify) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (userLinkSpotify.error) {
    throw Error(`Got error: ${JSON.stringify(userLinkSpotify.error)}`, {
      cause: userLinkSpotify.error
    });
  }
  if (!userLinkSpotify.token) {
    throw Error("Missing token", {
      cause: userLinkSpotify?.error || "unknown"
    });
  }
  await sync(client, userLinkSpotify.token);
  return {
    token: userLinkSpotify.token,
    isAuth: userLinkSpotify.isAuth,
    isEdited: userLinkSpotify.isEdited
  };
}
async function spotifyUnlink(client, data) {
  const { userUnLinkSpotify } = await client.sdk.userUnLinkSpotify({
    userEmail: data.email
  });
  if (!userUnLinkSpotify) {
    throw Error("Missing GraphQL response", {
      cause: "unknown"
    });
  }
  if (userUnLinkSpotify.error) {
    throw Error(`Got error: ${JSON.stringify(userUnLinkSpotify.error)}`, {
      cause: userUnLinkSpotify.error
    });
  }
  await sync(client, userUnLinkSpotify.token);
  return {
    token: userUnLinkSpotify.token,
    isAuth: userUnLinkSpotify.isAuth,
    isEdited: userUnLinkSpotify.isEdited
  };
}

// src/react/useIsomorphicLayoutEffect.ts
import { useEffect, useLayoutEffect } from "react";
var useIsomorphicLayoutEffect_default = typeof document === "undefined" ? useEffect : useLayoutEffect;

// src/react/context.tsx
var authContext = React.createContext(null);
function AuthProvider({
  children,
  client,
  token: initialToken
}) {
  const [token, setToken] = React.useState(() => initialToken);
  const [data, setData] = React.useState(void 0);
  useIsomorphicLayoutEffect_default(() => {
    function syncStore() {
      const storedData = store.get();
      setToken(storedData.token || void 0);
      setData(storedData.data || void 0);
    }
    syncStore();
    window.addEventListener(syncEventKey, syncStore, true);
    return () => window.removeEventListener(syncEventKey, syncStore, true);
  }, []);
  React.useEffect(() => {
    try {
      const appleSSOCleanup = loadAppleSSOScript();
      const googleSSOCleanup = loadGoogleSSOScript();
      return () => {
        appleSSOCleanup();
        googleSSOCleanup();
      };
    } catch (error) {
      console.error(error);
    }
  }, []);
  React.useEffect(() => {
    let unmounting = false;
    if (token) {
      const expired = isExpired(token);
      if (expired) {
        try {
          refresh(client, { token });
        } catch (error) {
          console.error("expired effect error", error);
          if (!unmounting) {
            setToken(void 0);
            setData(void 0);
          }
        }
      }
    }
    return () => {
      unmounting = true;
    };
  }, [client, token]);
  const authContextValue = React.useMemo(() => {
    return {
      client,
      token,
      data
    };
  }, [client, data, token]);
  return /* @__PURE__ */ React.createElement(authContext.Provider, { value: authContextValue }, children);
}
function useAuthContext() {
  const value = React.useContext(authContext);
  if (!value) {
    throw Error("Cannot call useAuthContext() outside of <AuthProvider />");
  }
  return value;
}

// src/react/hooks.ts
import { useMemo as useMemo2 } from "react";
function isAction(key) {
  return key in actions_exports;
}
function useAuth(options) {
  const { client, token, data } = useAuthContext();
  if (options?.required && (!token || !data)) {
    throw Error(
      "Called useAuth() with required option, but no session was found"
    );
  }
  const wrappedActions = useMemo2(() => {
    return new Proxy(actions_exports, {
      get(target, property) {
        if (!isAction(property)) {
          return Reflect.get(target, property);
        }
        return async (...args) => {
          return target[property].apply(target, [client, ...args]);
        };
      }
    });
  }, [client]);
  return {
    user: data,
    token,
    actions: wrappedActions
  };
}

// src/react/GoogleSSOButton.tsx
import React2 from "react";
var CLIENT_ID = "374001476805-jdr4d87slvoaobdajc0bas161r661rfs.apps.googleusercontent.com";
var SCRIPT_ID = "gapi_auth_lib";
var removeGoogleSSOScript = () => {
  const gscript = document.getElementById(SCRIPT_ID);
  if (gscript)
    document.body.removeChild(gscript);
};
var GoogleSSOButton = ({
  data,
  type = "icon",
  className = "",
  style = {},
  children,
  resultCallback
}) => {
  const { client } = useAuthContext();
  const [button, setButton] = React2.useState(
    /* @__PURE__ */ React2.createElement("button", { className, style }, children)
  );
  const getGoogleAuth = async (auth) => {
    const { credential } = auth;
    if (!credential)
      return void 0;
    let res;
    try {
      res = await socialLogin(client, {
        socialnetwork: "google",
        tokenId: credential,
        redirectUri: data?.redirectUri || `${window.location.origin}/en`
      });
      resultCallback({
        isValid: true,
        data: res
      });
    } catch (err) {
      resultCallback({
        isValid: false,
        error: err
      });
    }
    return res;
  };
  const loadGoogleSSOScript2 = () => new Promise((res, rej) => {
    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.type = "text/javascript";
    script.src = "https://accounts.google.com/gsi/client";
    script.id = SCRIPT_ID;
    document.body.appendChild(script);
    script.onerror = (error) => {
      rej(
        Error(
          `Error loading Google SSO script: ${JSON.stringify(error, null, 2)}`
        )
      );
    };
    script.onload = () => {
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: getGoogleAuth
      });
      createWrapper();
    };
  });
  const createWrapper = () => {
    const wrapper = document.createElement("div");
    wrapper.style.display = "none";
    wrapper.classList.add("custom-google-button");
    wrapper.setAttribute("id", "custom-google-button");
    document.body.appendChild(wrapper);
    window.google.accounts.id.renderButton(wrapper, {
      theme: "outline",
      size: "large",
      shape: "circle",
      type
    });
    const button2 = wrapper.querySelector("div[role=button]");
    const onClickFunction = { click: () => button2?.click() };
    const handleGoogleLogin = () => onClickFunction.click();
    const googleButton = /* @__PURE__ */ React2.createElement(
      "button",
      {
        className,
        style,
        onClick: () => handleGoogleLogin()
      },
      children
    );
    setButton(googleButton);
  };
  React2.useEffect(() => {
    loadGoogleSSOScript2();
    return () => removeGoogleSSOScript();
  }, []);
  return button;
};

// ../../node_modules/tslib/tslib.es6.js
var __assign = function() {
  __assign = Object.assign || function __assign2(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s)
        if (Object.prototype.hasOwnProperty.call(s, p))
          t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};

// ../../node_modules/graphql-tag/lib/index.js
import { parse } from "graphql";
var docCache = /* @__PURE__ */ new Map();
var fragmentSourceMap = /* @__PURE__ */ new Map();
var printFragmentWarnings = true;
var experimentalFragmentVariables = false;
function normalize(string) {
  return string.replace(/[\s,]+/g, " ").trim();
}
function cacheKeyFromLoc(loc) {
  return normalize(loc.source.body.substring(loc.start, loc.end));
}
function processFragments(ast) {
  var seenKeys = /* @__PURE__ */ new Set();
  var definitions = [];
  ast.definitions.forEach(function(fragmentDefinition) {
    if (fragmentDefinition.kind === "FragmentDefinition") {
      var fragmentName = fragmentDefinition.name.value;
      var sourceKey = cacheKeyFromLoc(fragmentDefinition.loc);
      var sourceKeySet = fragmentSourceMap.get(fragmentName);
      if (sourceKeySet && !sourceKeySet.has(sourceKey)) {
        if (printFragmentWarnings) {
          console.warn("Warning: fragment with name " + fragmentName + " already exists.\ngraphql-tag enforces all fragment names across your application to be unique; read more about\nthis in the docs: http://dev.apollodata.com/core/fragments.html#unique-names");
        }
      } else if (!sourceKeySet) {
        fragmentSourceMap.set(fragmentName, sourceKeySet = /* @__PURE__ */ new Set());
      }
      sourceKeySet.add(sourceKey);
      if (!seenKeys.has(sourceKey)) {
        seenKeys.add(sourceKey);
        definitions.push(fragmentDefinition);
      }
    } else {
      definitions.push(fragmentDefinition);
    }
  });
  return __assign(__assign({}, ast), { definitions });
}
function stripLoc(doc) {
  var workSet = new Set(doc.definitions);
  workSet.forEach(function(node) {
    if (node.loc)
      delete node.loc;
    Object.keys(node).forEach(function(key) {
      var value = node[key];
      if (value && typeof value === "object") {
        workSet.add(value);
      }
    });
  });
  var loc = doc.loc;
  if (loc) {
    delete loc.startToken;
    delete loc.endToken;
  }
  return doc;
}
function parseDocument(source) {
  var cacheKey = normalize(source);
  if (!docCache.has(cacheKey)) {
    var parsed = parse(source, {
      experimentalFragmentVariables,
      allowLegacyFragmentVariables: experimentalFragmentVariables
    });
    if (!parsed || parsed.kind !== "Document") {
      throw new Error("Not a valid GraphQL document.");
    }
    docCache.set(cacheKey, stripLoc(processFragments(parsed)));
  }
  return docCache.get(cacheKey);
}
function gql(literals) {
  var args = [];
  for (var _i = 1; _i < arguments.length; _i++) {
    args[_i - 1] = arguments[_i];
  }
  if (typeof literals === "string") {
    literals = [literals];
  }
  var result = literals[0];
  args.forEach(function(arg, i) {
    if (arg && arg.kind === "Document") {
      result += arg.loc.source.body;
    } else {
      result += arg;
    }
    result += literals[i + 1];
  });
  return parseDocument(result);
}
function resetCaches() {
  docCache.clear();
  fragmentSourceMap.clear();
}
function disableFragmentWarnings() {
  printFragmentWarnings = false;
}
function enableExperimentalFragmentVariables() {
  experimentalFragmentVariables = true;
}
function disableExperimentalFragmentVariables() {
  experimentalFragmentVariables = false;
}
var extras = {
  gql,
  resetCaches,
  disableFragmentWarnings,
  enableExperimentalFragmentVariables,
  disableExperimentalFragmentVariables
};
(function(gql_1) {
  gql_1.gql = extras.gql, gql_1.resetCaches = extras.resetCaches, gql_1.disableFragmentWarnings = extras.disableFragmentWarnings, gql_1.enableExperimentalFragmentVariables = extras.enableExperimentalFragmentVariables, gql_1.disableExperimentalFragmentVariables = extras.disableExperimentalFragmentVariables;
})(gql || (gql = {}));
gql["default"] = gql;
var lib_default = gql;

// src/graphql/generated.ts
var ArtistSetStatus = /* @__PURE__ */ ((ArtistSetStatus2) => {
  ArtistSetStatus2["Available"] = "AVAILABLE";
  ArtistSetStatus2["Reserved"] = "RESERVED";
  ArtistSetStatus2["Unavailable"] = "UNAVAILABLE";
  return ArtistSetStatus2;
})(ArtistSetStatus || {});
var CacheControlScope = /* @__PURE__ */ ((CacheControlScope2) => {
  CacheControlScope2["Private"] = "PRIVATE";
  CacheControlScope2["Public"] = "PUBLIC";
  return CacheControlScope2;
})(CacheControlScope || {});
var EventSort = /* @__PURE__ */ ((EventSort2) => {
  EventSort2["Date"] = "DATE";
  EventSort2["Venue"] = "VENUE";
  return EventSort2;
})(EventSort || {});
var ReunionFilter = /* @__PURE__ */ ((ReunionFilter2) => {
  ReunionFilter2["Confirmed"] = "confirmed";
  ReunionFilter2["Received"] = "received";
  return ReunionFilter2;
})(ReunionFilter || {});
var ReunionSort = /* @__PURE__ */ ((ReunionSort2) => {
  ReunionSort2["Date"] = "date";
  ReunionSort2["Location"] = "location";
  return ReunionSort2;
})(ReunionSort || {});
var TicketStatus = /* @__PURE__ */ ((TicketStatus2) => {
  TicketStatus2["Comingsoon"] = "COMINGSOON";
  TicketStatus2["Soldout"] = "SOLDOUT";
  return TicketStatus2;
})(TicketStatus || {});
var UserSignupDocument = lib_default`
  mutation userSignup($input: UserWebSignupInput!) {
    userSignup(input: $input) {
      token
      isValid
      error
    }
  }
`;
var UserWebLoginDocument = lib_default`
  mutation userWebLogin($email: String!, $password: String!) {
    userWebLogin(email: $email, password: $password) {
      token
      isValid
      isVerified
      error
    }
  }
`;
var UserDeleteAccountDocument = lib_default`
  mutation userDeleteAccount($email: String!) {
    userDeleteAccount(email: $email) {
      email
      isDeleted
      error
    }
  }
`;
var UserPasswordRecoverDocument = lib_default`
  mutation userPasswordRecover($email: String!, $sourceWeb: String) {
    userPasswordRecoverUserSendsEmail(email: $email, sourceWeb: $sourceWeb) {
      isValid
      email
      error
    }
  }
`;
var UserSocialLinkedDocument = lib_default`
  query userSocialLinked($email: String!) {
    userSocialLinked(email: $email) {
      valid
      socialnetwork
    }
  }
`;
var UserCheckEmailDocument = lib_default`
  query userCheckEmail($email: String!) {
    userCheckEmail(email: $email) {
      valid
    }
  }
`;
var UserWebVerificationDocument = lib_default`
  mutation userWebVerification($email: String!) {
    userWebVerification(email: $email) {
      isValid
      error
    }
  }
`;
var RefetchUserTokenDocument = lib_default`
  mutation refetchUserToken {
    refetchUserToken {
      isValid
      token
      error
    }
  }
`;
var UserSocialLoginDocument = lib_default`
  mutation userSocialLogin(
    $tokenId: String!
    $socialnetwork: String!
    $redirectUri: String
  ) {
    userSocialLogin(
      tokenId: $tokenId
      socialnetwork: $socialnetwork
      redirectUri: $redirectUri
    ) {
      isValid
      error
      token
      isVerified
      userDetails {
        email
        display_name
        name
        lastName
        userID
        country
        spotifyToken
      }
    }
  }
`;
var UserLinkSpotifyDocument = lib_default`
  mutation userLinkSpotify(
    $redirectUri: String!
    $tokenId: String!
    $userEmail: String!
  ) {
    userLinkSpotify(
      redirectUri: $redirectUri
      tokenId: $tokenId
      userEmail: $userEmail
    ) {
      isAuth
      isEdited
      token
      error
    }
  }
`;
var UserUnLinkSpotifyDocument = lib_default`
  mutation userUnLinkSpotify($userEmail: String!) {
    userUnLinkSpotify(userEmail: $userEmail) {
      isAuth
      isEdited
      token
      error
    }
  }
`;
var GetCurrentUserDocument = lib_default`
  mutation getCurrentUser {
    getCurrentUser {
      _id
      nickname
      lastname
      name
      imageUrl
      occupation
      business
      company
      inputCountry
      areFriends
      description
      url
      social {
        name
        link
      }
      isMentor
      customFields {
        userPreferences {
          topArtists {
            name
            image
            isSpotifyArtist
            spotifyId
            slug
          }
          musicalInterests
        }
        userFestivals
      }
    }
  }
`;
var defaultWrapper = (action, _operationName, _operationType) => action();
function getSdk(client, withWrapper = defaultWrapper) {
  return {
    userSignup(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(UserSignupDocument, variables, {
          ...requestHeaders,
          ...wrappedRequestHeaders
        }),
        "userSignup",
        "mutation"
      );
    },
    userWebLogin(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserWebLoginDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userWebLogin",
        "mutation"
      );
    },
    userDeleteAccount(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserDeleteAccountDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userDeleteAccount",
        "mutation"
      );
    },
    userPasswordRecover(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserPasswordRecoverDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userPasswordRecover",
        "mutation"
      );
    },
    userSocialLinked(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserSocialLinkedDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userSocialLinked",
        "query"
      );
    },
    userCheckEmail(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserCheckEmailDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userCheckEmail",
        "query"
      );
    },
    userWebVerification(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserWebVerificationDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userWebVerification",
        "mutation"
      );
    },
    refetchUserToken(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          RefetchUserTokenDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "refetchUserToken",
        "mutation"
      );
    },
    userSocialLogin(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserSocialLoginDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userSocialLogin",
        "mutation"
      );
    },
    userLinkSpotify(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserLinkSpotifyDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userLinkSpotify",
        "mutation"
      );
    },
    userUnLinkSpotify(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          UserUnLinkSpotifyDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "userUnLinkSpotify",
        "mutation"
      );
    },
    getCurrentUser(variables, requestHeaders) {
      return withWrapper(
        (wrappedRequestHeaders) => client.request(
          GetCurrentUserDocument,
          variables,
          { ...requestHeaders, ...wrappedRequestHeaders }
        ),
        "getCurrentUser",
        "mutation"
      );
    }
  };
}

// src/core/client.ts
import { GraphQLClient } from "graphql-request";
var AuthClient = class extends GraphQLClient {
  sdk;
  constructor(url, options) {
    super(url, options);
    this.sdk = getSdk(this);
  }
};
export {
  ArtistSetStatus,
  AuthClient,
  AuthProvider,
  CacheControlScope,
  EventSort,
  GetCurrentUserDocument,
  GoogleSSOButton,
  RefetchUserTokenDocument,
  ReunionFilter,
  ReunionSort,
  TicketStatus,
  UserCheckEmailDocument,
  UserDeleteAccountDocument,
  UserLinkSpotifyDocument,
  UserPasswordRecoverDocument,
  UserSignupDocument,
  UserSocialLinkedDocument,
  UserSocialLoginDocument,
  UserUnLinkSpotifyDocument,
  UserWebLoginDocument,
  UserWebVerificationDocument,
  actions_exports as actions,
  cookieKey,
  getSdk,
  useAuth
};
